import React, { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import emailjs from '@emailjs/browser';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import './Login.css';

type ViewMode = 'login' | 'register' | 'forgot' | 'reset';

// Initialize EmailJS
emailjs.init('t-hOGtWkQEw2R3yyO');

const Login: React.FC = () => {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token');
  
  const [viewMode, setViewMode] = useState<ViewMode>(tokenFromUrl ? 'reset' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [resetToken, setResetToken] = useState(tokenFromUrl || '');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (user) navigate('/home');
  }, [user, navigate]);

  React.useEffect(() => {
    // Check if there's a success message from password reset or other flows
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
    }
  }, [location]);

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/home');
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/register-owner`, {
        email,
        password,
        firstName,
        lastName,
      });

      if (response.data.success) {
        setSuccessMessage('Registration successful! Please log in.');
        setViewMode('login');
        // Clear registration fields
        setFirstName('');
        setLastName('');
        setPassword('');
        setConfirmPassword('');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });

      if (response.data.success && response.data.resetToken) {
        // Send email via EmailJS
        const resetUrl = `${window.location.origin}/login?token=${response.data.resetToken}`;
        
        try {
          const emailResult = await emailjs.send(
            'service_iuzsl6i',
            'template_a7lgdmn',
            {
              email: response.data.userEmail,
              link: resetUrl,
            }
          );
          
          console.log('Email sent successfully:', emailResult);
          setSuccessMessage('Password reset link has been sent to your email.');
          setEmail('');
        } catch (emailError: any) {
          console.error('EmailJS error:', emailError);
          setError(`Failed to send email: ${emailError.text || emailError.message || 'Unknown error'}`);
        }
      } else {
        setSuccessMessage('If an account with that email exists, a password reset link has been sent.');
      }
    } catch (err: any) {
      console.error('Forgot password error:', err);
      setError(err.response?.data?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/reset-password`, {
        token: resetToken,
        newPassword: password,
      });

      if (response.data.success) {
        setSuccessMessage('Password has been reset successfully! You can now log in.');
        setViewMode('login');
        setPassword('');
        setConfirmPassword('');
        setResetToken('');
        // Clear token from URL
        navigate('/login', { replace: true });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const switchView = (mode: ViewMode) => {
    setViewMode(mode);
    setError('');
    setSuccessMessage('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFirstName('');
    setLastName('');
  };

  return (
    <div className="alt2-page">
      <div className="alt2-container">
        {/* Left side - Image */}
        <div className="alt2-image">
          <img 
            src={process.env.PUBLIC_URL + '/images/18_graf_sign_2.jpg'} 
            alt="18 Graf Road" 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}  
            className="alt2-image-img"  
          />
        </div>

        {/* Right side - Form */}
        <div className="alt2-form-side">
          <div className="alt2-content">
            <h1 className="alt2-title">18 GRAF ROAD</h1>
            <p className="alt2-subtitle">
              {viewMode === 'register' && 'Owner Registration'}
              {viewMode === 'login' && 'Owners Portal'}
              {viewMode === 'forgot' && 'Reset Password'}
              {viewMode === 'reset' && 'Create New Password'}
            </p>

            {viewMode === 'login' && (
              // LOGIN FORM
              <form onSubmit={handleLoginSubmit} className="alt2-form">
                {successMessage && <div className="alt2-success">{successMessage}</div>}
                {error && <div className="alt2-error">{error}</div>}

                <div className="alt2-field">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="alt2-input"
                  />
                </div>

                <div className="alt2-field">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="alt2-input"
                  />
                </div>

                <button 
                  type="button" 
                  onClick={() => switchView('forgot')} 
                  className="alt2-forgot"
                >
                  Forgot Password?
                </button>

                <button type="submit" className="alt2-btn" disabled={loading}>
                  {loading ? 'Logging in...' : 'Log In'}
                </button>
              </form>
            )}

            {viewMode === 'register' && (
              // REGISTRATION FORM
              <form onSubmit={handleRegisterSubmit} className="alt2-form alt2-register-form">
                {error && <div className="alt2-error">{error}</div>}

                <div className="alt2-form-row">
                  <div className="alt2-field">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      autoComplete="given-name"
                      className="alt2-input"
                    />
                  </div>

                  <div className="alt2-field">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      type="text"
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      autoComplete="family-name"
                      className="alt2-input"
                    />
                  </div>
                </div>

                <div className="alt2-field">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="alt2-input"
                  />
                </div>

                <div className="alt2-field">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="alt2-input"
                    minLength={8}
                  />
                  <small className="alt2-password-note">Minimum 8 characters</small>
                </div>

                <div className="alt2-field">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="alt2-input"
                  />
                </div>

                <button type="submit" className="alt2-btn" disabled={loading}>
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>
              </form>
            )}

            {viewMode === 'forgot' && (
              // FORGOT PASSWORD FORM
              <form onSubmit={handleForgotPasswordSubmit} className="alt2-form">
                {successMessage && <div className="alt2-success">{successMessage}</div>}
                {error && <div className="alt2-error">{error}</div>}

                <p className="alt2-forgot-instructions">
                  Enter your email address and we'll send you a link to reset your password.
                </p>

                <div className="alt2-field">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="alt2-input"
                  />
                </div>

                <button type="submit" className="alt2-btn" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
            )}

            {viewMode === 'reset' && (
              // RESET PASSWORD FORM
              <form onSubmit={handleResetPasswordSubmit} className="alt2-form">
                {successMessage && <div className="alt2-success">{successMessage}</div>}
                {error && <div className="alt2-error">{error}</div>}

                <p className="alt2-forgot-instructions">
                  Enter your new password below.
                </p>

                <div className="alt2-field">
                  <label htmlFor="password">New Password</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="alt2-input"
                    minLength={8}
                  />
                  <small className="alt2-password-note">Minimum 8 characters</small>
                </div>

                <div className="alt2-field">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="alt2-input"
                  />
                </div>

                <button type="submit" className="alt2-btn" disabled={loading}>
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            )}

            <p className="alt2-footer">
              {viewMode === 'register' && (
                <>
                  Already have an account? <button onClick={() => switchView('login')} className="alt2-toggle-btn">Sign in</button>
                </>
              )}
              {viewMode === 'login' && (
                <>
                  Are you an owner? <button onClick={() => switchView('register')} className="alt2-toggle-btn">Create an account</button>
                </>
              )}
              {(viewMode === 'forgot' || viewMode === 'reset') && (
                <>
                  Remember your password? <button onClick={() => switchView('login')} className="alt2-toggle-btn">Sign in</button>
                </>
              )}
            </p>
            
            {viewMode === 'register' && (
              <p className="alt2-note">
                Note: Only owners whose email addresses are registered in the system can create accounts.
              </p>
            )}
            {viewMode === 'login' && (
              <p className="alt2-note">
                Please contact your building administrator if you need access.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
